/**
 * Circuit Breaker implementation to prevent request floods
 *
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Requests are blocked after failure threshold reached
 * - HALF_OPEN: Testing state after reset timeout, allows one request through
 */

type CircuitState = "closed" | "open" | "half-open";

interface CircuitBreakerConfig {
  /** Number of failures before opening the circuit */
  failureThreshold: number;
  /** Time in ms before attempting to close circuit again */
  resetTimeout: number;
  /** Optional name for logging */
  name?: string;
}

export class CircuitBreaker {
  private failures = 0;
  private state: CircuitState = "closed";
  private nextAttempt = 0;
  private config: CircuitBreakerConfig;

  constructor(config: CircuitBreakerConfig) {
    this.config = {
      failureThreshold: config.failureThreshold ?? 3,
      resetTimeout: config.resetTimeout ?? 30000,
      name: config.name ?? "unnamed",
    };
  }

  /**
   * Execute a function with circuit breaker protection
   */
  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === "open") {
      if (Date.now() < this.nextAttempt) {
        const error = new Error(
          `Circuit breaker is open for ${this.config.name}. Try again in ${Math.ceil((this.nextAttempt - Date.now()) / 1000)}s`
        );
        error.name = "CircuitBreakerOpenError";
        throw error;
      }
      // Time to test again
      this.state = "half-open";
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  /**
   * Check if the circuit is open (requests should be blocked)
   */
  isOpen(): boolean {
    if (this.state === "open" && Date.now() >= this.nextAttempt) {
      this.state = "half-open";
    }
    return this.state === "open";
  }

  /**
   * Get current circuit state
   */
  getState(): CircuitState {
    return this.state;
  }

  /**
   * Reset the circuit breaker to closed state
   */
  reset(): void {
    this.failures = 0;
    this.state = "closed";
    this.nextAttempt = 0;
  }

  private onSuccess(): void {
    this.failures = 0;
    this.state = "closed";
  }

  private onFailure(): void {
    this.failures++;

    if (this.failures >= this.config.failureThreshold) {
      this.state = "open";
      this.nextAttempt = Date.now() + this.config.resetTimeout;

      if (process.env.NODE_ENV === "development") {
        console.warn(
          `[CircuitBreaker] ${this.config.name} opened after ${this.failures} failures. ` +
            `Will retry in ${this.config.resetTimeout / 1000}s`
        );
      }
    }
  }
}

// Pre-configured circuit breakers for Instagram API endpoints
export const userInfoCircuitBreaker = new CircuitBreaker({
  failureThreshold: 3,
  resetTimeout: 30000,
  name: "user-info",
});

export const searchCreatorCircuitBreaker = new CircuitBreaker({
  failureThreshold: 3,
  resetTimeout: 30000,
  name: "search-creator",
});
